import React, { useState } from "react";
import { useAuth, useDispatch, useSelector, useTranslation } from "hooks";
import { Typography, Button, Icon, Tooltip, Tag } from "components";
import { RootState } from "configs/store/types";
import { setStatus, setLoading } from "configs/store/offline-mode/actions";
import {
  Assessment,
  Empty,
  Installing,
  OfflineDropdown,
  ShowPin,
  AcceptDialog,
} from "./components";
import { UsageQuota } from "./duck/types";
import { updateStorageSize } from "./duck/utils";
import {
  fetchAssignmentsReserve,
  fetchStudentAssignment,
  syncCachedAssignments,
  clearPWACache,
} from "./duck/operations";
import styles from "./OfflineTools.module.css";

const OfflineTools: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [auth] = useAuth();
  const [storageSize, setStorageSize] = React.useState<UsageQuota>({
    usage: "0 bytes",
    quota: "0 bytes",
  });
  const [acceptDialogOpened, setAcceptDialogOpened] = useState<boolean>(false);
  const [progress] = useState<string>("");
  const { assignments, swStatus, isOnline, status, loading } = useSelector(
    (state: RootState) => state.offlineMode,
  );

  React.useEffect(() => {
    if (
      swStatus === "installed" &&
      isOnline &&
      auth.data.user.offlineMode.access
    ) {
      fetchAssignmentsReserve(auth.data.user).then((assignmentsIds) => {
        if (assignmentsIds.length > 0) {
          dispatch(setLoading(true));
          fetchStudentAssignment({
            dispatch,
            assignmentsIds,
            auth,
            t,
            current: 0,
          });
        }
        syncCachedAssignments(auth.data.user.md5GlobalId, dispatch);
      });
    }
  }, [swStatus, isOnline]);

  React.useEffect(() => {
    updateStorageSize(setStorageSize);
  }, [assignments, swStatus, loading]);

  React.useEffect(() => {
    if (!loading) {
      window.setTimeout(() => {
        dispatch(setStatus(""));
        updateStorageSize(setStorageSize);
      }, 3000);
    }
  }, [loading]);

  const clearHandler = (): void => {
    clearPWACache(dispatch, auth.data.user.md5GlobalId).then();
    if (!localStorage.getItem("hasDeletedAssignmentsFromCache")) {
      localStorage.setItem("hasDeletedAssignmentsFromCache", "true");
    }
  };

  // comment for DEV
  if (
    process.env.NODE_ENV !== "production" ||
    !auth.data.user.offlineMode.access
  ) {
    return null;
  }

  if (swStatus === "activating") {
    return <Installing />;
  }

  return (
    <>
      {!isOnline && (
        <Tooltip position="bottom" content={t("offline.offlineDescription")}>
          <Tag tabIndex={0} type="danger" className={styles.offline}>
            <Icon
              name="disconnect-outlined"
              size="small"
              className={styles.offlineIcon}
            />
            {t("offline.mode")}
          </Tag>
        </Tooltip>
      )}
      <OfflineDropdown progress={progress} loading={loading}>
        <div id="iframe-container" className={styles.iframeContainer} />
        <div className={styles.content}>
          <div className={styles.storage}>
            {navigator.storage && (
              <div className={styles.storageInfo}>
                <div className={styles.storageData}>
                  <Typography variant="c1">
                    {t("offline.usageQuota", {
                      usage: storageSize.usage,
                      quota: storageSize.quota,
                    })}
                  </Typography>
                  <Button
                    prefix={<Icon name="delete-outlined" size="small" />}
                    size="small"
                    className={styles.clearButton}
                    variant="clear"
                    shape="rounded"
                    onClick={() => setAcceptDialogOpened(true)}
                  >
                    {t("offline.clearAll")}
                  </Button>
                </div>
              </div>
            )}
            <ShowPin />
            {status && <div className={styles.status}>{status}</div>}
          </div>

          <ul className={styles.list}>
            {assignments.map((assignment: any) => (
              <Assessment key={assignment.id} data={assignment} />
            ))}
          </ul>
          {assignments.length === 0 && <Empty />}
        </div>
      </OfflineDropdown>
      <AcceptDialog
        opened={acceptDialogOpened}
        setOpened={setAcceptDialogOpened}
        accept={clearHandler}
      />
    </>
  );
};

export default OfflineTools;
